/**
 * Plan vs Actual — manager / executive view of plan targets vs actuals
 * with variance and RAG color logic.
 *
 * Tries the live /api/plan-vs-actual/me endpoint first (AE-scoped). If that
 * fails or the user is manager/executive, renders an aggregate manager view
 * with by-AE rows. Demo data is tagged with a "DEMO" pill.
 *
 * Locked 4-color rule (May 5, 2026):
 *   green ≥95% / yellow 80-94% / amber 60-79% / red <60% of plan-to-date.
 *
 * Palette: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No emojis, no exclamation points.
 */
import { useEffect, useState } from "react";
import { api, getSession, getCurrentTenant } from "@/lib/session";
import OperatorShell from "@/components/operator/OperatorShell";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const BG = "#FAFAF7";

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt$(v: number): string {
  if (!v && v !== 0) return "$0";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(0)}%`;
}

type Color = "green" | "yellow" | "amber" | "red";

function colorFor(pct: number): Color {
  if (pct >= 0.95) return "green";
  if (pct >= 0.80) return "yellow";
  if (pct >= 0.60) return "amber";
  return "red";
}

const COLOR_MAP: Record<Color, { bg: string; text: string; label: string; dot: string }> = {
  green: { bg: "#D1FAE5", text: "#065F46", label: "On plan", dot: "#16A34A" },
  yellow: { bg: "#FEF3C7", text: "#92400E", label: "Slightly behind", dot: "#EAB308" },
  amber: { bg: "#FED7AA", text: "#9A3412", label: "Behind plan", dot: "#EA580C" },
  red: { bg: "#FECACA", text: "#991B1B", label: "Significantly behind", dot: "#DC2626" },
};

// ── Demo seed — tenant 2 (Mi6 Demo) only. Real tenants get live data only. ──

interface AeRow {
  name: string;
  role: "ae" | "bdr";
  quotaYTD: number;
  closedYTD: number;
  weightedPipeline: number;
  trendPriorPct: number; // attainment last quarter, for trend arrow
}

const DEMO_AE_ROWS: AeRow[] = [
  { name: "Jordan Calloway", role: "ae", quotaYTD: 500_000, closedYTD: 510_000, weightedPipeline: 720_000, trendPriorPct: 0.92 },
  { name: "Priya Sharma",    role: "ae", quotaYTD: 395_000, closedYTD: 348_000, weightedPipeline: 560_000, trendPriorPct: 0.88 },
  { name: "Marcus Webb",     role: "ae", quotaYTD: 420_000, closedYTD: 252_000, weightedPipeline: 410_000, trendPriorPct: 0.71 },
  { name: "Devon Carter",    role: "ae", quotaYTD: 360_000, closedYTD: 165_000, weightedPipeline: 290_000, trendPriorPct: 0.55 },
];

interface PeriodTargets {
  label: string;
  quotaTarget: number;
  pipelineTarget: number;
  dealCountTarget: number;
  mqlTarget: number;
}

const PERIOD_TARGETS: Record<"month" | "quarter" | "year", PeriodTargets> = {
  month:   { label: "May 2026",       quotaTarget: 220_000,   pipelineTarget: 660_000,   dealCountTarget: 4,  mqlTarget: 35  },
  quarter: { label: "Q2 2026",        quotaTarget: 700_000,   pipelineTarget: 2_100_000, dealCountTarget: 12, mqlTarget: 110 },
  year:    { label: "Plan Year 2026", quotaTarget: 1_675_000, pipelineTarget: 5_025_000, dealCountTarget: 42, mqlTarget: 420 },
};

// Demo aggregate actuals per period (Q2 = May current month)
const PERIOD_ACTUALS: Record<"month" | "quarter" | "year", { quota: number; pipeline: number; deals: number; mql: number }> = {
  month:   { quota: 188_000,   pipeline: 612_000,   deals: 3,  mql: 31  },
  quarter: { quota: 622_000,   pipeline: 1_980_000, deals: 11, mql: 96  },
  year:    { quota: 1_275_000, pipeline: 4_690_000, deals: 36, mql: 372 },
};

// ── Live API shape ───────────────────────────────────────────────────────────

interface PlanVsActualResponse {
  year: number;
  month: number;
  annual_quota: number;
  plan_ytd: number;
  closed_ytd: number;
  weighted_pipeline: number;
  gap: number;
  color: "green" | "yellow" | "amber" | "red" | "none";
  attainment_pct: number | null;
  has_plan: boolean;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function DemoPill() {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 99,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: "#FEF3C7",
        color: "#92400E",
        marginLeft: 8,
        verticalAlign: "middle",
      }}
    >
      Demo data
    </span>
  );
}

function StatusPill({ color, attainmentPct }: { color: Color; attainmentPct: number }) {
  const c = COLOR_MAP[color];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.dot }} />
      {fmtPct(attainmentPct)} · {c.label}
    </span>
  );
}

function TrendArrow({ current, prior }: { current: number; prior: number }) {
  const delta = current - prior;
  if (Math.abs(delta) < 0.02) {
    return <span style={{ color: "#6B6B70", fontSize: 11 }}>→ flat</span>;
  }
  if (delta > 0) {
    return (
      <span style={{ color: "#065F46", fontSize: 11, fontWeight: 600 }}>
        ↑ {(delta * 100).toFixed(0)} pts
      </span>
    );
  }
  return (
    <span style={{ color: "#991B1B", fontSize: 11, fontWeight: 600 }}>
      ↓ {(Math.abs(delta) * 100).toFixed(0)} pts
    </span>
  );
}

function KpiCard({
  label,
  target,
  actual,
  unit = "$",
}: {
  label: string;
  target: number;
  actual: number;
  unit?: "$" | "n";
}) {
  const hasTarget = target > 0;
  const pct = hasTarget ? actual / target : 0;
  const color = colorFor(pct);
  const c = COLOR_MAP[color];
  const variance = actual - target;
  const fmt = (v: number) => (unit === "$" ? fmt$(v) : v.toString());
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 8,
        padding: 16,
        position: "relative",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#6B6B70",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: BLACK,
          marginTop: 6,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmt(actual)}
      </div>
      <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 2 }}>
        {hasTarget ? <>of {fmt(target)} target</> : "No target set"}
      </div>
      {hasTarget ? (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <StatusPill color={color} attainmentPct={pct} />
          <span
            style={{
              fontSize: 11,
              color: variance >= 0 ? "#065F46" : "#991B1B",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {variance >= 0 ? "+" : ""}
            {unit === "$" ? fmt$(variance) : variance}
          </span>
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 11, color: "#6B6B70" }}>
          Set a target in the plan to activate variance tracking.
        </div>
      )}
      {/* progress bar */}
      <div
        style={{
          marginTop: 10,
          height: 6,
          background: "#EFEFEE",
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.min(100, pct * 100).toFixed(1)}%`,
            height: "100%",
            background: c.dot,
            borderRadius: 99,
            transition: "width 0.4s",
          }}
        />
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

// Bug-B fix (2026-05-20): shape from the fixed /api/leaderboard
interface LiveLeaderboardRow {
  ae_user_id: string;
  full_name: string;
  email: string;
  role: string;
  quota_annual: number;
  won_value: number;
  weighted_pipeline: number;
}

export default function PlanVsActual() {
  const session = getSession();
  const role = session?.role ?? "manager";
  const [period, setPeriod] = useState<"month" | "quarter" | "year">("quarter");
  const [livePva, setLivePva] = useState<PlanVsActualResponse | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);
  // Bug-B fix: live AE rows for manager/executive BY AE table
  const [liveAeRows, setLiveAeRows] = useState<AeRow[]>([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await api<PlanVsActualResponse>("/api/plan-vs-actual/me");
        if (!cancel) setLivePva(d);
      } catch (e: any) {
        if (!cancel) setLiveError(e?.message ?? "live data unavailable");
      } finally {
        if (!cancel) setLiveLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const isManagerView = role === "manager" || role === "executive";

  // Bug-B fix: fetch live per-AE rows from the leaderboard API for manager view
  useEffect(() => {
    if (!isManagerView) return;
    let cancel = false;
    (async () => {
      try {
        const rows = await api<LiveLeaderboardRow[]>("/api/leaderboard");
        if (cancel) return;
        const mapped: AeRow[] = rows
          .filter((r) => r.role === "ae" || r.role === "manager")
          .map((r) => ({
            name: r.full_name || r.email,
            role: r.role as "ae" | "bdr",
            quotaYTD: Number(r.quota_annual) || 0,
            closedYTD: Number(r.won_value) || 0,
            weightedPipeline: Number(r.weighted_pipeline) || 0,
            trendPriorPct: 0,
          }));
        setLiveAeRows(mapped);
      } catch {
        // silent — fall back to demo seed for demo tenants
      }
    })();
    return () => { cancel = true; };
  }, [isManagerView]);

  const tenantCtx = getCurrentTenant();
  const isDemo = tenantCtx?.is_demo === true;
  // Use live AE rows when available; fall back to demo seed for demo tenants only
  const aeRows: AeRow[] = liveAeRows.length > 0 ? liveAeRows : (isDemo ? DEMO_AE_ROWS : []);
  const t = PERIOD_TARGETS[period];
  const a = isDemo ? PERIOD_ACTUALS[period] : { quota: 0, pipeline: 0, deals: 0, mql: 0 };

  // For AE view, prefer live numbers when available
  const aeQuotaTarget = livePva?.plan_ytd ?? t.quotaTarget;
  const aeQuotaActual = livePva?.closed_ytd ?? a.quota;
  const aePipeline = livePva?.weighted_pipeline ?? a.pipeline;
  const aePipelineTarget = t.pipelineTarget;
  const usingLive = !!livePva && !liveError;

  // Manager rollup for the AE table
  const totalQuota = aeRows.reduce((s, r) => s + r.quotaYTD, 0);
  const totalClosed = aeRows.reduce((s, r) => s + r.closedYTD, 0);
  const totalPipeline = aeRows.reduce((s, r) => s + r.weightedPipeline, 0);
  const overallPct = totalQuota > 0 ? totalClosed / totalQuota : 0;

  return (
    <OperatorShell breadcrumbs={[{ label: "Plan vs Actual" }]}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Title bar */}
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: BLACK, margin: 0 }}>
              Plan vs Actual
              {!usingLive && <DemoPill />}
            </h1>
            <p style={{ fontSize: 13, color: "#6B6B70", margin: "4px 0 0 0" }}>
              {isManagerView
                ? "Team performance against plan, by AE and rolled up. Variance, attainment, and trend vs prior period."
                : "Your plan-to-date, closed, weighted forecast, and gap. Locked 4-color rule applies."}
              {liveError && (
                <span style={{ marginLeft: 8, color: "#92400E", fontSize: 12 }}>
                  Live API unreachable — showing demo data.
                </span>
              )}
            </p>
          </div>

          {/* Period switcher */}
          <div role="tablist" style={{ display: "inline-flex", gap: 4, background: "#FFFFFF", border: "1px solid #E5E5E7", borderRadius: 8, padding: 3 }}>
            {(["month", "quarter", "year"] as const).map((p) => (
              <button
                key={p}
                role="tab"
                aria-selected={period === p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  border: "none",
                  cursor: "pointer",
                  background: period === p ? BLACK : "transparent",
                  color: period === p ? "#FFFFFF" : "#525258",
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 12, color: "#6B6B70", marginTop: 10, marginBottom: 18 }}>
          Period: <span style={{ fontWeight: 600, color: BLACK }}>{t.label}</span>
        </div>

        {/* Hero status strip */}
        <div
          style={{
            background: BLACK,
            color: "#FFFFFF",
            borderRadius: 8,
            padding: "18px 22px",
            marginBottom: 20,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 18,
          }}
        >
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Overall attainment
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {fmtPct(isManagerView ? overallPct : (aeQuotaTarget > 0 ? aeQuotaActual / aeQuotaTarget : 0))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Closed {period === "year" ? "YTD" : period}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {fmt$(isManagerView ? totalClosed : aeQuotaActual)}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>
              {(isManagerView ? totalQuota : aeQuotaTarget) > 0
                ? <>of {fmt$(isManagerView ? totalQuota : aeQuotaTarget)} target</>
                : "No quarter target set"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Weighted pipeline
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {fmt$(isManagerView ? totalPipeline : aePipeline)}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>
              {((isManagerView ? totalPipeline : aePipeline) / Math.max(1, isManagerView ? totalQuota - totalClosed : aeQuotaTarget - aeQuotaActual)).toFixed(1)}× coverage of remaining gap
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Gap to plan
            </div>
            {(isManagerView ? totalQuota : aeQuotaTarget) > 0 ? (
              <>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    marginTop: 4,
                    fontVariantNumeric: "tabular-nums",
                    color: (isManagerView ? totalClosed >= totalQuota : aeQuotaActual >= aeQuotaTarget) ? "#86EFAC" : "#FCA5A5",
                  }}
                >
                  {(isManagerView ? totalClosed >= totalQuota : aeQuotaActual >= aeQuotaTarget) ? "+" : "-"}
                  {fmt$(Math.abs(isManagerView ? totalClosed - totalQuota : aeQuotaActual - aeQuotaTarget))}
                </div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>
                  {(isManagerView ? totalClosed >= totalQuota : aeQuotaActual >= aeQuotaTarget) ? "ahead of plan" : "behind plan"}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, opacity: 0.6 }}>
                  —
                </div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>
                  Set a target to track gap
                </div>
              </>
            )}
          </div>
        </div>

        {/* KPI grid: 4 strips */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
            marginBottom: 22,
          }}
        >
          <KpiCard label="Quota attainment" target={t.quotaTarget} actual={a.quota} unit="$" />
          <KpiCard label="Pipeline coverage" target={t.pipelineTarget} actual={a.pipeline} unit="$" />
          <KpiCard label="Deals closed" target={t.dealCountTarget} actual={a.deals} unit="n" />
          <KpiCard label="MQL volume" target={t.mqlTarget} actual={a.mql} unit="n" />
        </div>

        {/* Manager: by-AE table */}
        {isManagerView && (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 8,
              overflow: "hidden",
              marginBottom: 22,
            }}
          >
            <div
              style={{
                background: BLACK,
                color: "#FFFFFF",
                padding: "10px 16px",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>By AE — plan-to-date</span>
              <span style={{ fontSize: 10, opacity: 0.7 }}>{aeRows.length} aes</span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FAFAF7", borderBottom: "1px solid #E5E5E7" }}>
                  <th style={th}>AE</th>
                  <th style={thNum}>Quota YTD</th>
                  <th style={thNum}>Closed</th>
                  <th style={thNum}>Variance</th>
                  <th style={thNum}>Pipeline</th>
                  <th style={th}>Status</th>
                  <th style={th}>Trend vs Q1</th>
                </tr>
              </thead>
              <tbody>
                {aeRows.map((row) => {
                  const pct = row.quotaYTD > 0 ? row.closedYTD / row.quotaYTD : 0;
                  const color = colorFor(pct);
                  const variance = row.closedYTD - row.quotaYTD;
                  return (
                    <tr key={row.name} style={{ borderBottom: "1px solid #EFEFEE" }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: BLACK }}>{row.name}</div>
                        <div style={{ fontSize: 11, color: "#6B6B70", textTransform: "uppercase", letterSpacing: "0.06em" }}>{row.role}</div>
                      </td>
                      <td style={tdNum}>{fmt$(row.quotaYTD)}</td>
                      <td style={tdNum}>{fmt$(row.closedYTD)}</td>
                      <td style={{ ...tdNum, color: variance >= 0 ? "#065F46" : "#991B1B", fontWeight: 600 }}>
                        {variance >= 0 ? "+" : ""}
                        {fmt$(variance)}
                      </td>
                      <td style={tdNum}>{fmt$(row.weightedPipeline)}</td>
                      <td style={td}>
                        <StatusPill color={color} attainmentPct={pct} />
                      </td>
                      <td style={td}>
                        <TrendArrow current={pct} prior={row.trendPriorPct} />
                      </td>
                    </tr>
                  );
                })}
                {/* total row */}
                <tr style={{ background: "#FAFAF7", borderTop: "2px solid #E5E5E7" }}>
                  <td style={{ ...td, fontWeight: 700, color: BLACK }}>Team total</td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{fmt$(totalQuota)}</td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{fmt$(totalClosed)}</td>
                  <td style={{ ...tdNum, fontWeight: 700, color: totalClosed - totalQuota >= 0 ? "#065F46" : "#991B1B" }}>
                    {totalClosed - totalQuota >= 0 ? "+" : ""}
                    {fmt$(totalClosed - totalQuota)}
                  </td>
                  <td style={{ ...tdNum, fontWeight: 700 }}>{fmt$(totalPipeline)}</td>
                  <td style={td}>
                    <StatusPill color={colorFor(overallPct)} attainmentPct={overallPct} />
                  </td>
                  <td style={td} />
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Color rule legend */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: "#525258", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Color rule
          </div>
          {(["green", "yellow", "amber", "red"] as Color[]).map((k) => {
            const c = COLOR_MAP[k];
            const range = k === "green" ? "≥95%" : k === "yellow" ? "80-94%" : k === "amber" ? "60-79%" : "<60%";
            return (
              <div key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: c.dot }} />
                <span style={{ fontSize: 12, color: c.text, fontWeight: 600 }}>{c.label}</span>
                <span style={{ fontSize: 11, color: "#6B6B70" }}>{range}</span>
              </div>
            );
          })}
          <div style={{ marginLeft: "auto", fontSize: 11, color: "#8E8E94" }}>Locked May 5, 2026</div>
        </div>

        {liveLoading && (
          <div style={{ fontSize: 12, color: "#6B6B70", marginBottom: 12 }}>Checking live data…</div>
        )}
        {!liveLoading && !isDemo && aeRows.length === 0 && (
          <div
            style={{
              marginTop: 16,
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 8,
              padding: "16px 20px",
              fontSize: 13,
              color: "#6B6B70",
            }}
          >
            Plan vs Actual populates when AEs have annual quotas and accounts in motion. Verify plans in Settings → Sales Plans.
          </div>
        )}
      </div>
    </OperatorShell>
  );
}

const th: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: "#525258",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const thNum: React.CSSProperties = { ...th, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const td: React.CSSProperties = { padding: "10px 14px", verticalAlign: "middle" };
const tdNum: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: BLACK };
